import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config';
import styles from './ArtifactStatusDisplay.module.css';

interface ArtifactItemStatus {
  id: string;
  current_state: {
    id: string;
    name: string;
    state_type?: string;
  };
  is_improvement?: boolean;
  is_operational?: boolean;
}

interface ArtifactStatusDisplayProps {
  artifactId: string;
  source?: 'artifact' | 'artifact-item';
  className?: string;
}

/**
 * Display artifact status from either the artifact itself or its improvement item
 */
export const ArtifactStatusDisplay: React.FC<ArtifactStatusDisplayProps> = ({
  artifactId,
  source = 'artifact-item',
  className = '',
}) => {
  const [status, setStatus] = useState<ArtifactItemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      // Don't fetch if artifactId is invalid
      if (!artifactId || artifactId === 'undefined' || artifactId === 'unknown') {
        console.log('[ArtifactStatusDisplay] Skipping status fetch - invalid artifactId:', artifactId);
        setStatus(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        if (source === 'artifact-item') {
          // Fetch improvement artifact item for this artifact
          const url = `${API_BASE_URL}/artifacts/${artifactId}/items?is_improvement=true&limit=1`;
          console.log('[ArtifactStatusDisplay] Fetching status from:', url);
          
          const response = await fetch(url);
          
          console.log('[ArtifactStatusDisplay] Response received:', {
            status: response.status,
            ok: response.ok,
            contentType: response.headers.get('content-type')
          });
          
          if (!response.ok) {
            console.warn('[ArtifactStatusDisplay] API request failed:', {
              status: response.status,
              statusText: response.statusText,
              url: response.url
            });
            // Don't throw error for 404 - just means no items exist yet
            if (response.status === 404) {
              setStatus(null);
              setLoading(false);
              return;
            }
            throw new Error(`Failed to fetch artifact status (${response.status})`);
          }

          // Check if response is actually JSON
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.warn('[ArtifactStatusDisplay] Response is not JSON:', contentType);
            // Silently fail - endpoint might not exist yet
            setStatus(null);
            setLoading(false);
            return;
          }

          let data;
          try {
            data = await response.json();
          } catch (parseError) {
            console.error('[ArtifactStatusDisplay] Failed to parse JSON response:', parseError);
            // Silently fail - don't throw error to avoid breaking the UI
            setStatus(null);
            setLoading(false);
            return;
          }
          
          if (data && data.length > 0) {
            setStatus(data[0]);
          } else {
            // No improvement item found - artifact may not be in development yet
            setStatus(null);
          }
        } else {
          // Fetch artifact operational status
          const url = `${API_BASE_URL}/artifacts/${artifactId}`;
          console.log('[ArtifactStatusDisplay] Fetching artifact from:', url);
          
          const response = await fetch(url);
          
          console.log('[ArtifactStatusDisplay] Response received:', {
            status: response.status,
            ok: response.ok,
            contentType: response.headers.get('content-type')
          });
          
          if (!response.ok) {
            console.warn('[ArtifactStatusDisplay] API request failed:', {
              status: response.status,
              statusText: response.statusText,
              url: response.url
            });
            // Don't throw error for 404 - just means artifact doesn't exist
            if (response.status === 404) {
              setStatus(null);
              setLoading(false);
              return;
            }
            throw new Error(`Failed to fetch artifact (${response.status})`);
          }

          // Check if response is actually JSON
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.warn('[ArtifactStatusDisplay] Response is not JSON:', contentType);
            // Silently fail - endpoint might not exist yet
            setStatus(null);
            setLoading(false);
            return;
          }

          let artifact;
          try {
            artifact = await response.json();
          } catch (parseError) {
            console.error('[ArtifactStatusDisplay] Failed to parse JSON response:', parseError);
            // Silently fail - don't throw error to avoid breaking the UI
            setStatus(null);
            setLoading(false);
            return;
          }
          
          setStatus({
            id: artifact.id,
            current_state: {
              id: artifact.id,
              name: artifact.is_operational ? 'Operational' : 'In Development',
            },
            is_operational: artifact.is_operational,
          });
        }
      } catch (err) {
        console.error('[ArtifactStatusDisplay] Error fetching status:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [artifactId, source]);

  if (loading) {
    return (
      <div className={`${styles.artifactStatusDisplay} ${styles.loading} ${className}`}>
        <div className={styles.statusLabel}>Status:</div>
        <div className={styles.statusValue}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${styles.artifactStatusDisplay} ${styles.error} ${className}`}>
        <div className={styles.statusLabel}>Status:</div>
        <div className={`${styles.statusValue} ${styles.error}`}>{error}</div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className={`${styles.artifactStatusDisplay} ${styles.noStatus} ${className}`}>
        <div className={styles.statusLabel}>Status:</div>
        <div className={styles.statusValue}>Not Started</div>
      </div>
    );
  }

  const getStatusClass = () => {
    if (status.is_operational) return styles.operational;
    if (status.is_improvement) return styles.inDevelopment;
    return '';
  };

  return (
    <div className={`${styles.artifactStatusDisplay} ${getStatusClass()} ${className}`}>
      <div className={styles.statusLabel}>Status:</div>
      <div className={styles.statusValue}>
        {status.current_state?.name || 'Unknown'}
      </div>
      {status.is_operational !== undefined && (
        <div className={styles.operationalIndicator}>
          {status.is_operational ? '✓ Operational' : '⚠ In Development'}
        </div>
      )}
    </div>
  );
};

